import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import {
  Image,
  IMAGE_EXTENSIONS,
  MAX_PRODUCT_DESCRIPTION_LENGTH,
  MAX_PRODUCT_TITLE_LENGTH,
  SelectOption,
} from "@apptractive/stm-shared";
import { isEmpty } from "lodash";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  WBAutocomplete,
  WBS3MediaDragDrop,
  WBButton,
  WBTextField,
  WBImageList,
  WBAlert,
  WBFlex,
  WBForm,
  WBSelect,
  WBModal,
  WBChip,
} from "@apptractive/design-system-web";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { Option, ProductStatus } from "../../API";
import { Link } from "../../components/Link";
import { SelectOptionsForm } from "../../components/SelectOptionsForm";
import { SubscriptionStatusAlert } from "../../components/SubscriptionStatusAlert";
import { CSProductFragment as PRODUCT_FRAGMENT } from "../../customGraphql/fragments";
import { CSGetSub as GET_SUB } from "../../customGraphql/queries";

import {
  createProduct as CREATE_PRODUCT,
  updateProduct as UPDATE_PRODUCT,
  deleteProduct as DELETE_PRODUCT,
} from "../../graphql/mutations";
import {
  getProduct as GET_PRODUCT,
  optionsByGroup,
  getSubscriptionStatus as GET_SUBSCRIPTION_STATUS,
  getUser as GET_USER,
} from "../../graphql/queries";
import { PATHS } from "../../navigation/paths";

const ProductForm = () => {
  const { t } = useTranslation();
  const { data: subData } = useQuery(gql(GET_SUB));
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const [images, setImages] = useState<Image[] | []>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [showTags, setShowTags] = useState(false);
  const [showLocations, setShowLocations] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });
  const { title, description } = watch();

  const { data: productData } = useQuery(gql(GET_PRODUCT), {
    variables: {
      id: productId,
    },
    skip: !productId,
  });

  const [
    createProduct,
    {
      loading: createLoading,
      data: createData,
      error: createError,
      reset: resetCreate,
    },
  ] = useMutation(gql(CREATE_PRODUCT), {
    update(cache, { data }) {
      cache.modify({
        fields: {
          productsByUser(existing = []) {
            const dataRef = cache.writeFragment({
              data: data.createProduct,
              fragment: gql(PRODUCT_FRAGMENT),
            });

            return { items: [existing.items.concat(dataRef)] };
          },
        },
      });
    },
  });

  const [
    updateProduct,
    {
      loading: updateLoading,
      data: updateData,
      error: updateError,
      reset: resetUpdate,
    },
  ] = useMutation(gql(UPDATE_PRODUCT));

  const [
    deleteProduct,
    { data: deleteData, error: deleteError, loading: deleteLoading },
  ] = useMutation(gql(DELETE_PRODUCT), {
    update(cache, { data }) {
      const id = data?.deleteProduct?.id;
      cache.modify({
        fields: {
          productsByUser(existing = []) {
            return {
              items: [existing.items.filter((item: any) => item.id !== id)],
            };
          },
        },
      });
    },
  });

  const [
    getSubscriptionStatus,
    {
      data: subscriptionStatusData,
      //loading: subscriptionStatusLoading
    },
  ] = useLazyQuery(gql(GET_SUBSCRIPTION_STATUS), {
    variables: {},
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  const { data: tagsData } = useQuery(gql(optionsByGroup), {
    notifyOnNetworkStatusChange: true,
    variables: {
      group: category,
    },
    skip: !category,
  });

  const { data: categoriesData } = useQuery(gql(optionsByGroup), {
    notifyOnNetworkStatusChange: true,
    variables: {
      group: "Categories",
    },
  });

  const { data: locationsData } = useQuery(gql(optionsByGroup), {
    notifyOnNetworkStatusChange: true,
    variables: {
      group: `locations-${category}`,
    },
  });

  const { data: userData } = useQuery(gql(GET_USER), {
    variables: {
      id: subData?.sub,
    },
  });

  const subscriptionStatus = useMemo(
    () => subscriptionStatusData?.getSubscriptionStatus || {},
    [subscriptionStatusData]
  );

  const product = useMemo(() => productData?.getProduct || {}, [productData]);
  const tags = useMemo(
    () =>
      tagsData?.optionsByGroup?.items?.map((option: Option) => ({
        ...option,
        label: t(option.value, { ns: "options" }),
      })) || [],
    [tagsData, t]
  );

  const categories = useMemo(
    () => categoriesData?.optionsByGroup?.items || [],
    [categoriesData]
  );

  const locations = useMemo(
    () => locationsData?.optionsByGroup?.items || [],
    [locationsData]
  );

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  const inputs = useMemo(
    () => ({
      title: {
        label: t("title", { ns: "common" }),
        placeholder: t("titlePlaceholder", { ns: "common" }),
        name: "title",
        type: "text",
        defaultValue: "",
        rules: {
          required: t("titleRequired", { ns: "common" }),
        },
      },
      description: {
        label: t("description", { ns: "common" }),
        placeholder: t("descriptionPlaceholder", { ns: "common" }),
        name: "description",
        type: "text",
        defaultValue: "",
        rules: {
          required: t("descriptionRequired", { ns: "common" }),
        },
      },
      category: {
        label: t("category", { ns: "common" }),
        placeholder: t("categoryPlaceholder", { ns: "common" }),
        name: "category",
        type: "text",
        options: categories,
        defaultValue: "",
        rules: {
          required: t("categoryRequired", { ns: "common" }),
        },
      },
      expiresAt: {
        label: t("expireDatesTitle", { ns: "common" }),
        placeholder: t("expiresPlaceholder", { ns: "common" }),
        name: "expiresAt",
        type: "text",
        options: [
          {
            label: t("24 hours", { ns: "options" }),
            value: 1,
          },
          {
            label: t("3 days", { ns: "options" }),
            value: 3,
          },
          {
            label: t("7 days", { ns: "options" }),
            value: 7,
          },
          {
            label: t("14 days", { ns: "options" }),
            value: 14,
          },
          {
            label: t("30 days", { ns: "options" }),
            value: 30,
          },
        ],
        defaultValue: "",
        rules: {
          required: !productId && t("expiresRequired", { ns: "common" }),
        },
      },
    }),
    [t, categories, productId]
  );

  useEffect(() => {
    getSubscriptionStatus();
  }, [getSubscriptionStatus]);

  useEffect(() => {
    if (!isEmpty(createData?.createProduct) || !isEmpty(deleteData)) {
      reset();
      setImages([]);
      setSelectedTags([]);
      setSelectedLocations([]);
      getSubscriptionStatus();
    }
  }, [createData, deleteData, reset, getSubscriptionStatus]);

  useEffect(() => {
    if (!isEmpty(tags) && !isEmpty(product)) {
      if (!isEmpty(product?.images)) {
        setImages(
          product.images.map(({ identityId, key }: Image) => ({
            src: null,
            identityId,
            key,
          }))
        );
      }

      if (product?.title) {
        setValue("title", product.title, { shouldValidate: true });
      }

      if (product?.description) {
        setValue("description", product.description, {
          shouldValidate: true,
        });
      }

      if (product?.tags) {
        setSelectedTags(product?.tags);
      }

      if (product?.locations) {
        setSelectedLocations(product?.locations);
      }

      if (product?.category) {
        setValue("category", product.category, {
          shouldValidate: true,
        });
      }
    }
  }, [product, tags, setValue, locations]);

  const onImageChange = async (image: Image) => {
    setImages((prevState) => [...prevState, image]);

    if (productId) {
      await trigger();
    }
  };

  const onImageDelete = async (index: number) => {
    setImages((prevImages) =>
      [...prevImages].filter((img, ind) => ind !== index)
    );

    if (productId) {
      await trigger();
    }
  };

  /**
   * Handle form submit
   */
  const onSubmit = async (data: any) => {
    const input = {
      ...data,
      images: images.map((image) => ({
        key: image.key,
        identityId: image.identityId,
        level: "protected",
      })),
    };

    if (productId) {
      try {
        let updateParams = {
          ...input,
          id: productId,
          tags: selectedTags,
          locations: selectedLocations,
          category: category || data.category.value,
        };

        if (data.expiresAt) {
          updateParams = {
            ...updateParams,
            expiresAt: DateTime.local().plus({ days: data.expiresAt }).toISO(),
          };
        } else {
          const { expiresAt: _expiresAt, ...update } = updateParams;
          updateParams = update;
        }

        await updateProduct({
          variables: {
            input: updateParams,
          },
        });
      } catch (err) {
        console.log("ERROR updating product: ", err);
      }
    } else {
      try {
        await createProduct({
          variables: {
            input: {
              ...input,
              status:
                category === "CH" ? ProductStatus.REVIEW : ProductStatus.ACTIVE,
              tags: selectedTags,
              locations: selectedLocations,
              category: category || data.category.value,
              country: category || null,
              teamId: user.teamId,
              expiresAt: DateTime.local()
                .plus({ days: data.expiresAt })
                .toISO(),
              // owner: `${sub}::${sub}`  // TODO: bug aws not setting owner on server  https://github.com/aws-amplify/amplify-cli/issues/10245 https://github.com/aws-amplify/amplify-category-api/issues/37
            },
          },
        });
      } catch (err) {
        console.log("ERROR creating product:", err);
      }
    }
  };

  const onDelete = async () => {
    const confirm = window.confirm(
      t("confirmDeleteProductMessage", { ns: "products" })
    );

    if (confirm) {
      try {
        await deleteProduct({
          variables: {
            input: {
              id: productId,
            },
          },
        });

        reset();
      } catch (err) {
        console.log("ERROR deleting product:", err);
      }
    }
  };

  const onSaveTags = (options: Option[]) => {
    setSelectedTags(options.map((option: Option) => option.value) || []);
    setShowTags(false);
  };

  const onSaveLocations = (options: Option[]) => {
    setSelectedLocations(options.map((option: Option) => option.value) || []);
    setShowLocations(false);
  };

  return (
    <>
      <WBModal
        title={t(selectedTags?.length > 0 ? "updateTags" : "selectTags", {
          ns: "common",
        })}
        open={showTags}
        onClose={() => setShowTags(false)}
        sx={{
          width: "95%",
          height: "95%",
        }}
      >
        <SelectOptionsForm
          selectedOptions={selectedTags}
          category={category!}
          onSubmit={onSaveTags}
          hideSelectAll
        />
      </WBModal>
      {category !== "CH" && (
        <WBModal
          title={t(
            selectedLocations?.length > 0
              ? "updateLocationsTitle"
              : "selectLocationsTitle",
            {
              ns: "onboarding",
            }
          )}
          open={showLocations}
          onClose={() => setShowLocations(false)}
          sx={{
            width: "95%",
          }}
        >
          <SelectOptionsForm
            selectedOptions={selectedLocations}
            category={`locations-${category}`}
            onSubmit={onSaveLocations}
          />
        </WBModal>
      )}
      <WBForm onSubmit={handleSubmit(onSubmit)}>
        <WBS3MediaDragDrop
          validFileTypes={IMAGE_EXTENSIONS}
          inputAccept="images/*"
          onImageUpload={onImageChange}
          maxFiles={6}
          maxSizeMB={0.1}
          uploadMessage={t("dragDropImages", { ns: "common" })}
          uploadBtnText={t("selectImages", { ns: "common" })}
          maxWidthOrHeight={500}
        />
        <WBFlex>
          <WBImageList
            images={images}
            actionIcon="Close"
            onActionClick={onImageDelete}
          />
        </WBFlex>
        <Controller
          control={control}
          name={inputs.title.name}
          rules={inputs.title.rules}
          defaultValue={inputs.title.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type="text"
              label={inputs.title.label}
              inputProps={{
                maxLength: MAX_PRODUCT_TITLE_LENGTH,
              }}
              //placeholder={inputs.title.placeholder}
              error={!!(errors.title && errors.title.message)}
              helperText={
                errors?.title?.message ||
                `${
                  MAX_PRODUCT_TITLE_LENGTH - title?.length >= 0
                    ? MAX_PRODUCT_TITLE_LENGTH - title?.length
                    : !title
                    ? MAX_PRODUCT_TITLE_LENGTH
                    : 0
                } ${t("charactersLeft", { ns: "common" })}`
              }
              margin="dense"
            />
          )}
        />

        <Controller
          control={control}
          name={inputs.description.name}
          rules={inputs.description.rules}
          defaultValue={inputs.description.defaultValue}
          render={({ field }) => (
            <WBTextField
              {...field}
              type="text"
              label={inputs.description.label}
              multiline
              inputProps={{
                maxLength: MAX_PRODUCT_DESCRIPTION_LENGTH,
              }}
              rows={4}
              //placeholder={inputs.description.placeholder}
              error={!!errors?.description?.message}
              helperText={
                errors?.description?.message ||
                `${
                  MAX_PRODUCT_DESCRIPTION_LENGTH - description?.length >= 0
                    ? MAX_PRODUCT_DESCRIPTION_LENGTH - description?.length
                    : !description
                    ? MAX_PRODUCT_DESCRIPTION_LENGTH
                    : 0
                } ${t("charactersLeft", { ns: "common" })}`
              }
              margin="dense"
            />
          )}
        />

        <WBButton
          sx={{ mt: 1 }}
          variant="outlined"
          type="button"
          onClick={() => setShowTags(true)}
        >
          {t(selectedTags?.length > 0 ? "updateTags" : "selectTags", {
            ns: "common",
          })}
        </WBButton>
        <WBFlex flexWrap="wrap">
          {selectedTags?.map((option) => (
            <WBChip
              key={option}
              label={option}
              variant="filled"
              sx={{
                m: 1,
                background: "linear-gradient(135deg, #63b4f6, #78E9D3)",
                color: "#2C4052",
              }}
            />
          ))}
        </WBFlex>

        {category !== "CH" && (
          <>
            <WBButton
              sx={{ mt: 1 }}
              variant="outlined"
              type="button"
              onClick={() => setShowLocations(true)}
            >
              {t(
                selectedLocations?.length > 0
                  ? "updateLocationsTitle"
                  : "selectLocationsTitle",
                {
                  ns: "onboarding",
                }
              )}
            </WBButton>
            <WBFlex flexWrap="wrap">
              {selectedLocations?.map((option) => (
                <WBChip
                  key={option}
                  color="secondary"
                  label={option}
                  variant="filled"
                  sx={{
                    m: 1,
                    background: "linear-gradient(135deg, #63b4f6, #78E9D3)",
                    color: "#2C4052",
                  }}
                />
              ))}
            </WBFlex>
          </>
        )}

        {!category && (
          <Controller
            control={control}
            name={inputs.category.name}
            rules={inputs.category.rules}
            defaultValue={inputs.category.defaultValue}
            render={({ field: { onChange, ...props } }) => (
              <WBAutocomplete
                {...props}
                onChange={(data: SelectOption) => onChange(data)}
                options={inputs.category.options}
                renderInput={(params: any) => (
                  <WBTextField
                    {...params}
                    margin="dense"
                    placeholder={inputs.category.placeholder}
                    label={inputs.category.label}
                    error={!!(errors.category && errors.category.message)}
                    helperText={
                      (errors.category && errors.category.message) || ""
                    }
                  />
                )}
              />
            )}
          />
        )}
        <Controller
          control={control}
          name={inputs.expiresAt.name}
          rules={inputs.expiresAt.rules}
          defaultValue={inputs.expiresAt.defaultValue}
          render={({ field: { onChange, ...props } }) => (
            <WBSelect
              {...props}
              onChange={(e) => onChange(e?.target?.value)}
              label={inputs.expiresAt.label}
              options={inputs.expiresAt.options}
              error={!!(errors.expiresAt && errors.expiresAt.message)}
              helperText={(errors.expiresAt && errors.expiresAt.message) || ""}
              margin="dense"
            />
          )}
        />

        {(createError?.message ||
          updateError?.message ||
          deleteError?.message) && (
          <WBAlert
            title={
              createError?.message ||
              updateError?.message ||
              deleteError?.message
            }
            severity="error"
            sx={{ my: 2 }}
          />
        )}

        {!isEmpty(createData) && (
          <WBAlert
            title={t("createdSuccessfully", { ns: "common" })}
            severity="success"
            onClose={resetCreate}
            sx={{ mt: 2 }}
          >
            <Link to={`${PATHS.products}?userId=${subData?.sub}`} replace>
              {t("viewProducts", { ns: "products" })}
            </Link>
          </WBAlert>
        )}

        {!isEmpty(updateData) && (
          <WBAlert
            title={t("updatedSuccessfully", { ns: "common" })}
            severity="success"
            onClose={resetUpdate}
            sx={{ mt: 2 }}
          >
            <Link to={`${PATHS.products}?userId=${subData?.sub}`} replace>
              {t("viewProducts", { ns: "products" })}
            </Link>
          </WBAlert>
        )}

        {!isEmpty(deleteData) && (
          <WBAlert
            title={t("deletedSuccessfully", { ns: "common" })}
            severity="success"
            onClose={resetCreate}
            sx={{ mt: 2 }}
          >
            <Link to={`${PATHS.products}?userId=${subData?.sub}`} replace>
              {t("viewProducts", { ns: "products" })}
            </Link>
          </WBAlert>
        )}

        {!productId && (
          <SubscriptionStatusAlert
            remaining={subscriptionStatus?.monthlyAccessQuantity}
            titleRemaining={t("monthlyAccessRemaining", { ns: "purchase" })}
            titleUpgrade={t("upgradePlanAdditionalMonthlyAccess", {
              ns: "purchase",
            })}
            titleMaximum={t("maximumMonthlyAccess", { ns: "purchase" })}
            isOwner={subscriptionStatus?.isOwner}
            isExpired={subscriptionStatus?.isExpired}
            canUpgrade={subscriptionStatus?.canUpgrade}
            isBrand
          />
        )}

        <WBFlex>
          <WBButton
            loading={productId ? updateLoading : createLoading}
            disabled={
              (subscriptionStatus?.monthlyAccessQuantity === 0 ||
                subscriptionStatus?.isExpired) &&
              !productId
            }
            sx={{
              mt: 2,
            }}
          >
            {productId
              ? t("updateTitle", { ns: "common" })
              : t("createTitle", { ns: "common" })}
          </WBButton>
          <Box component="span" mr={1} />
          {productId && (
            <WBButton
              type="button"
              color="error"
              onClick={onDelete}
              loading={deleteLoading}
              sx={{
                mt: 2,
              }}
            >
              {t("deleteTitle", { ns: "common" })}
            </WBButton>
          )}
        </WBFlex>
      </WBForm>
    </>
  );
};

export default ProductForm;
